import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RecaptchaBadge from '../../components/RecaptchaBadge';
import { APP_PATH_LOGIN, MAIN_APP_NAME } from '../../config';
import { useForgotPassword } from '../../hooks/useForgotPassword';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    minHeight: '100%'
  },
  grid: {
    height: '100%'
  },
  quoteContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.background.default}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  quote: {
    backgroundColor: theme.palette.neutral,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  quoteInner: {
    textAlign: 'center',
    flexBasis: '600px'
  },
  contentContainer: {
    height: '100%'
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  contentBody: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center'
    }
  },
  form: {
    paddingLeft: 100,
    paddingRight: 100,
    paddingBottom: 125,
    flexBasis: 700,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  title: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(2)
  },
  submitButton: {
    margin: theme.spacing(2, 0)
  }
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const { formState, handleChange, handleBack, handleSubmit, hasError, isLoading, isSuccess } =
    useForgotPassword();

  return (
    <div className={classes.root}>
      <Grid className={classes.grid} container justifyContent="center">
        <Grid className={classes.quoteContainer} item lg={5}>
          <div className={classes.quote}>
            <Typography 
              variant="h1" 
              color="textPrimary"
              style={{ fontSize: '3rem', fontWeight: 700 }}>
              {MAIN_APP_NAME}
            </Typography>
          </div>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <Typography className={classes.title} variant="h1">
                Reset Your Password
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid className={classes.contentContainer} item lg={7} xs={12}>
          <div className={classes.content}>
            <div className={classes.contentHeader}>
              <IconButton onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div className={classes.contentBody}>
              <form className={classes.form} onSubmit={handleSubmit}>
                <Typography className={classes.title} variant="h2">
                  Forgot Password
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Enter your email address to receive a password reset link
                </Typography>
                {isSuccess && (
                  <Typography color="primary" gutterBottom style={{ marginTop: 16 }}>
                    If an account with that email exists, you will receive a password reset link.
                  </Typography>
                )}
                <TextField
                  className={classes.textField}
                  data-test="email-forgot-password"
                  error={hasError('email')}
                  fullWidth
                  helperText={
                    hasError('email') ? formState.errors.email[0] : null
                  }
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.email || ''}
                  variant="outlined"
                />
                <Button
                  className={classes.submitButton}
                  color="primary"
                  data-test="submit-forgot-password"
                  disabled={!formState.isValid || isLoading}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Typography color="textSecondary" variant="body1">
                  Remember your password?{' '}
                  <Link
                    component={RouterLink}
                    data-test="back-to-login"
                    to={APP_PATH_LOGIN}
                    variant="h6">
                    Sign in
                  </Link>
                </Typography>
                <RecaptchaBadge />
              </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ForgotPassword;

